// src/api/serviceOutage.js
//
// Der geteilte Erreichbarkeits-Waechter (UI2 Teil 9, Karte Z-1a).
//
// WAS ER TUT
// ----------
// Ein Deploy auf Render laesst jeden `/api/`-Aufruf kurz mit 502/503/504 oder
// einem Netzfehler enden. Statt dass jede Flaeche ihre eigene Fehlerform zeigt
// (das Foto des Owners: FUENF Formen auf EINER Seite), erkennt dieses Modul den
// Zustand "Ausfall" an EINER Stelle und meldet ihn ueber `abonniere()`. Das
// Banner haengt daran; die Theme-Regel (E-3) blendet waehrend des Zustands die
// Fehler-Alerts im Seiteninhalt aus.
//
// DER UNTERSCHIED ZU `BackendStatusContext.jsx` (nicht als Vorbild nehmen!)
// ------------------------------------------------------------------------
// Jener pollte im Sekundentakt auf einen Kaltstart hin, den es nicht mehr gibt,
// und wird nirgends montiert. Dieses Modul hat KEINEN Timer, solange kein
// Fehlschlag gemeldet wird — es ist rein ereignisgetrieben. Der einzige Timer
// ist der Rueckzug WAEHREND eines Ausfalls.
//
// DIE DREI ZUSTAENDE
// ------------------
//   online  — der Normalfall. Kein Timer, keine Sonde.
//   ausfall — bestaetigt durch eine rote Sonde gegen `/api/health`. Setzt die
//             Klasse `pog-ausfall` an `document.body` (E-3) und pollt mit
//             Rueckzug nach.
//   offline — der Rechner hat KEIN Netz (`navigator.onLine === false`). 🔴 Das
//             ist NICHT der Ausfall (E-11): der Dienst laeuft die ganze Zeit,
//             nur der Laptop sieht ihn nicht. Keine Sonde (sie koennte ohne
//             Netz nichts messen), KEINE `pog-ausfall`-Klasse, eigener Wortlaut.
//             Verlassen wird der Zustand ueber die `online`/`offline`-Ereignisse.
//             🔴 Der Uebergang ist an DREI Stellen abgeriegelt (R15-W1): der
//             `offline`-Listener nimmt einen ausstehenden Sonden-Timer mit,
//             `starteSonde` steigt bei `istOffline()` aus, und ein Sondenlauf,
//             der WAEHREND des Netzverlusts rot zurueckkommt, setzt `offline`
//             statt `ausfall`.
//
// WARUM DIE SONDE UEBER `_origFetch` FAEHRT
// -----------------------------------------
// Ein `fetch('/api/health')` liefe durch den `window.fetch`-Mantel
// (`index.js`): es wartete erst auf einen Token-Abruf (bevor das Zeitlimit
// greift) und liefe mit seinem EIGENEN Fehlschlag erneut in `merkeFehlschlag`
// (Rueckkopplung). Deshalb bekommt dieses Modul die Originalreferenz ueber
// `setzeOriginalFetch()` gereicht — NICHT ueber einen Import: `index.js`
// definiert `_origFetch` nach seinem Importblock, ein Import liefe in die TDZ.
//
// WARUM `cache: 'no-store'` AN JEDER SONDE
// ----------------------------------------
// `/api/health` ist `jsonify({"status":"ok"})` OHNE `Cache-Control`, `ETag`
// oder `Last-Modified`. Ein heuristisch zwischengespeichertes 200 koennte die
// Sonde "gruen" melden, waehrend der Dienst rot ist — dann bliebe der Banner
// stehen, obwohl er weggehoert. Ein Wort, kein Risiko.
//
// 🔴 EIN ABBRUCH IST KEIN AUSFALL (Nachbesserung 3, 2026-10-10)
// ------------------------------------------------------------
// Gemessen am 2026-10-10 auf einer GESUNDEN Seite (`/rma/1888`): das einzige
// Ereignis auf dem Netz war die Sonde selbst (504 aus dem lokalen CRA-Proxy).
// Etwas zaehlte einen Fehlschlag, den das Netz nicht zeigt — ein ABBRUCH.
//
// Der Weg: `useCaseLive`/`useOverviewPolling` brechen ihren laufenden Aufruf im
// Effekt-Aufraeumer ab (StrictMode montiert jeden Effekt ZWEIMAL). `AbortController`
// laesst axios mit `CanceledError` abweisen — OHNE Antwort, also `status == null`,
// also nach der alten Regel ein "Netzfehler". Ist der Abbruch frueher als der
// Adapter, geht ueberhaupt kein Paket hinaus: kein `requestfailed`, kein 5xx,
// nichts, woran man ihn im Netz sehen koennte. Der Waechter faehrt dann eine
// Sonde gegen einen Dienst, der die ganze Zeit erreichbar war.
//
// `istAbbruch()` schliesst ihn aus — an ALLEN drei Haken, weil der Fehler dort
// ankommt, wo gezaehlt wird (`merkeFehlschlag(status, url, fehler)`).
//
// ⚠️ Was dabei NICHT verlorengeht: ein Zeitlimit, das ein Aufrufer SELBST ueber
// einen `AbortController` zieht, ist von einem Abbruch nicht zu unterscheiden und
// faellt mit heraus. Die Sonde deckt diesen Fall ab — sie hat ihr eigenes
// Zeitlimit (`SONDE_TIMEOUT_MS`) und meldet dann rot.

import axios from 'axios';
import { isCrossOriginUrl } from './authHelpers';

/** Die HTTP-Stati, die einen Ausfall ausloesen koennen. */
const AUSFALL_STATUS = [502, 503, 504];

/** Zeitlimit EINES Sondenaufrufs. */
export const SONDE_TIMEOUT_MS = 2500;

/** Der Rueckzug zwischen zwei Sondenversuchen, in Millisekunden. Der letzte Wert ist der Deckel. */
export const RUECKZUG_MS = [3000, 5000, 10000, 20000, 30000];

/** Die drei Zustaende. */
export const ZUSTAND = { ONLINE: 'online', AUSFALL: 'ausfall', OFFLINE: 'offline' };

/** Die Klasse, an der die E-3-Theme-Regel haengt (`body.pog-ausfall main &`). */
export const AUSFALL_KLASSE = 'pog-ausfall';

// ── Die unverpackte Originalreferenz ────────────────────────────────────────
// Beim Laden zeigt sie auf das echte `window.fetch` (der Mantel ist zu diesem
// Zeitpunkt noch nicht gesetzt). `index.js` ueberschreibt sie nach seinem
// eigenen `_origFetch` ueber den Setter — das ist derselbe Wert.
let origFetch =
  typeof window !== 'undefined' && typeof window.fetch === 'function'
    ? window.fetch.bind(window)
    : null;

/**
 * Reicht die unverpackte `fetch`-Referenz herein (aus `index.js`, nach `_origFetch`).
 * @param {Function} fn — das echte `window.fetch`
 */
export function setzeOriginalFetch(fn) {
  if (typeof fn === 'function') origFetch = fn;
}

// ── Zustand ─────────────────────────────────────────────────────────────────
let zustand = ZUSTAND.ONLINE;
let seit = null;
let sondeLaeuft = false;
let rueckzugStufe = 0;
let sondeTimer = null;

const abonnenten = new Set();

function istOffline() {
  return typeof navigator !== 'undefined' && navigator.onLine === false;
}

function setzeKlasse(an) {
  if (typeof document === 'undefined' || !document.body) return;
  if (an) document.body.classList.add(AUSFALL_KLASSE);
  else document.body.classList.remove(AUSFALL_KLASSE);
}

function setzeZustand(neu) {
  if (neu === zustand) return;
  const alt = zustand;
  zustand = neu;
  seit = neu === ZUSTAND.ONLINE ? null : Date.now();

  // E-3: die Klasse steht GENAU im Zustand "Ausfall" — nicht offline (E-11).
  if (neu === ZUSTAND.AUSFALL) setzeKlasse(true);
  else if (alt === ZUSTAND.AUSFALL) setzeKlasse(false);

  abonnenten.forEach((fn) => {
    try { fn(zustand, seit); } catch { /* ein Abonnent darf den Rest nicht kippen */ }
  });
}

// ── Die Erkenner-Regel ──────────────────────────────────────────────────────

/** Pfad einer URL ohne Herkunft, ohne Query und ohne Fragment. */
function pfadVon(url) {
  if (!url || typeof url !== 'string') return '';
  let pfad = url;
  if (/^https?:\/\//i.test(url)) {
    try { pfad = new URL(url).pathname; } catch { return ''; }
  }
  return pfad.split('?')[0].split('#')[0];
}

/** Beginnt der PFAD der URL mit `/api/` (oder ist genau `/api`)? */
export function istApiPfad(url) {
  const pfad = pfadVon(url);
  return pfad === '/api' || pfad.startsWith('/api/');
}

/**
 * Ist dieser Fehlschlag ein Ausfall-Kandidat?
 *
 * Bedingungen, alle gemeinsam: Status 502/503/504 ODER ein Netzfehler
 * (`status == null`), die URL ist NICHT cross-origin (relative URLs sind
 * ausdruecklich eingeschlossen — auf Prod ist `REACT_APP_API_BASE_URL=""`), und
 * ihr Pfad beginnt mit `/api/`.
 *
 * @param {number|null|undefined} status
 * @param {string} url
 * @returns {boolean}
 */
export function istAusfallKandidat(status, url) {
  const istNetzfehler = status == null;
  const istAusfallStatus = AUSFALL_STATUS.includes(status);
  if (!istNetzfehler && !istAusfallStatus) return false;
  if (isCrossOriginUrl(url || '')) return false;
  return istApiPfad(url);
}

/**
 * Ist dieser Fehlschlag ein ABBRUCH — also kein Ausfall?
 *
 * Ein Abbruch sagt NICHTS ueber den Dienst: die Gegenseite hat nie geantwortet,
 * weil die Anfrage zurueckgezogen wurde (Effekt-Aufraeumer, StrictMode-Doppelmontage,
 * Seitenwechsel, ein eigenes Zeitlimit). Er sieht am Fehlerobjekt wie ein
 * Netzfehler aus (`status == null`), und genau deshalb braucht die Zaehlung
 * dieses Merkmal.
 *
 * Vier Anker, absichtlich ueberlappend: `fetch` mit `signal` weist mit
 * `AbortError` ab (DOMException), axios 1.x mit `CanceledError`/`ERR_CANCELED`
 * (`axios/lib/cancel/CanceledError.js`), und `axios.isCancel` ist der
 * dokumentierte Weg dorthin (er prueft `__CANCEL__`).
 *
 * ⚠️ **Gemeint ist der Abbruch des AUFRUFERS, nicht ein Abbruch auf der
 * Netzebene.** Ein `route.abort()` in Playwright oder ein Seitenwechsel sind
 * Netzfehler der Gegenseite: Chromium laesst `fetch` dafuer mit `TypeError`
 * abweisen, nicht mit `AbortError` — sie zaehlen weiterhin (genau das misst
 * `ui2-teil9-ausfall.spec.ts` Z1.2, der zweite Weg in den Ausfall).
 *
 * @param {*} fehler — das abgewiesene Fehlerobjekt; `null`/`undefined` ist kein Abbruch
 * @returns {boolean}
 */
export function istAbbruch(fehler) {
  if (!fehler) return false;
  if (fehler.name === 'AbortError') return true;
  if (fehler.name === 'CanceledError') return true;
  if (fehler.code === 'ERR_CANCELED') return true;
  return axios.isCancel?.(fehler) === true;
}

// ── Die Sonde ───────────────────────────────────────────────────────────────

/** Ein einzelner Sondenaufruf. Gruen heisst `response.ok`; alles andere ist rot. */
async function sondiere() {
  if (typeof origFetch !== 'function') return false;
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const limit = controller ? setTimeout(() => controller.abort(), SONDE_TIMEOUT_MS) : null;
  try {
    const antwort = await origFetch('/api/health', {
      // W-13: kein heuristischer Cache — sonst meldete ein altes 200 "gruen".
      cache: 'no-store',
      signal: controller ? controller.signal : undefined,
    });
    return !!(antwort && antwort.ok);
  } catch {
    return false;
  } finally {
    if (limit) clearTimeout(limit);
  }
}

/** Den naechsten Rueckzugsschritt abholen (und die Stufe hochziehen). */
function naechsterRueckzug() {
  const i = Math.min(rueckzugStufe, RUECKZUG_MS.length - 1);
  rueckzugStufe += 1;
  return RUECKZUG_MS[i];
}

function planeSonde() {
  if (sondeTimer) clearTimeout(sondeTimer);
  sondeTimer = setTimeout(() => {
    sondeTimer = null;
    starteSonde();
  }, naechsterRueckzug());
}

/**
 * Startet einen Sondenlauf. "Sonde laeuft" wird SYNCHRON gesetzt, bevor der
 * Aufruf beginnt — so kann ein Fehlschlag, der waehrend des Laufs eintrifft,
 * keine zweite Sonde starten (Entprellung auf den Zustand, W-14).
 */
async function starteSonde() {
  if (sondeLaeuft) return;
  // 🔴 R15-W1 (Nachbesserung 4, 2026-10-11): der Rechner kann WAEHREND des
  // Rueckzugs offline gegangen sein. Ohne diese Zeile liefe die geplante Sonde
  // gegen ein Netz, das es nicht gibt, saehe rot — und setzte den Zustand auf
  // `ausfall` ZURUECK, mit Banner „Forge is restarting" ohne Netz. Genau der
  // Fall, den E-11 verbietet: der Dienst LAEUFT, nur der Laptop sieht ihn nicht.
  // Die zweite Haelfte desselben Riegels steht am `offline`-Listener unten (er
  // nimmt einen AUSSTEHENDEN Timer mit) — beide braucht es: der Listener faengt
  // den geplanten Lauf, diese Zeile den, der schon angestossen war.
  if (istOffline()) {
    setzeZustand(ZUSTAND.OFFLINE);
    return;
  }
  sondeLaeuft = true;
  let gruen = false;
  try {
    gruen = await sondiere();
  } finally {
    sondeLaeuft = false;
  }
  if (gruen) {
    rueckzugStufe = 0;
    setzeZustand(ZUSTAND.ONLINE);
  } else if (istOffline()) {
    // Dritter Gurt: das Netz kann WAEHREND des Sondenlaufs weggefallen sein.
    // Eine rote Sonde ohne Netz belegt nichts ueber den Dienst.
    setzeZustand(ZUSTAND.OFFLINE);
  } else {
    setzeZustand(ZUSTAND.AUSFALL);
    planeSonde();
  }
}

// ── Die drei oeffentlichen Melder ───────────────────────────────────────────

/**
 * Meldet einen Fehlschlag. Zaehlt nur, wenn `istAusfallKandidat` zutrifft.
 *
 * 🔴 Ein ABBRUCH zaehlt NIE (`istAbbruch`): er belegt nichts ueber den Dienst.
 * Wer den Fehler hat, gibt ihn als dritten Wert mit — sonst ist ein Abbruch von
 * einem Netzfehler nicht zu unterscheiden (beide haben `status == null`).
 *
 * 🔴 Ist der Rechner offline (`navigator.onLine === false`), geht der
 * Fehlschlag NICHT in die Zaehlung: kein `pog-ausfall`, keine Sonde — statt
 * dessen der eigene Zustand `offline` (E-11).
 *
 * Entprellung: der erste Fehlschlag startet die Sonde SOFORT. Solange der
 * Zustand "Ausfall" steht oder ein Sondenaufruf aussteht, startet kein
 * Fehlschlag eine zweite Sonde — auch nicht im Rueckzugsfenster zwischen zwei
 * Versuchen.
 *
 * @param {number|null|undefined} status — `null`/`undefined` heisst "keine Antwort"
 * @param {string} url
 * @param {*} [fehler] — das Fehlerobjekt, wenn es eines gibt; nur damit ist ein
 *   Abbruch erkennbar
 */
export function merkeFehlschlag(status, url, fehler) {
  if (istAbbruch(fehler)) return;
  if (!istAusfallKandidat(status, url)) return;
  if (istOffline()) {
    setzeZustand(ZUSTAND.OFFLINE);
    return;
  }
  if (zustand === ZUSTAND.AUSFALL || sondeLaeuft || sondeTimer) return;
  starteSonde();
}

/**
 * Meldet eine erfolgreiche Antwort. Nur eine same-origin `/api/`-Antwort zaehlt:
 * eine erfolgreiche Supabase- oder Fremdhost-Anfrage sagt nichts darueber, ob
 * Forge erreichbar ist.
 *
 * Ist der Zustand "Ausfall" und kommt eine /api/-Antwort durch, ist der Dienst
 * nachweislich wieder da — dann wird sofort erholt, statt auf den naechsten
 * Rueckzugsschritt zu warten.
 */
export function merkeErfolg(url) {
  if (isCrossOriginUrl(url || '') || !istApiPfad(url)) return;
  if (zustand === ZUSTAND.AUSFALL) {
    if (sondeTimer) { clearTimeout(sondeTimer); sondeTimer = null; }
    rueckzugStufe = 0;
    setzeZustand(ZUSTAND.ONLINE);
  } else if (zustand === ZUSTAND.ONLINE) {
    rueckzugStufe = 0;
  }
}

/** Sofort eine Sonde fahren (z. B. aus einem "erneut versuchen"-Weg). */
export function probiereJetzt() {
  if (sondeTimer) { clearTimeout(sondeTimer); sondeTimer = null; }
  starteSonde();
}

// ── Abfragen + Abos ─────────────────────────────────────────────────────────

/** Der aktuelle Zustand als Zeichenkette — der Schnappschuss fuer `useSyncExternalStore`. */
export function aktuellerZustand() {
  return zustand;
}

/** Steht gerade ein Ausfall? */
export function istAusfall() {
  return zustand === ZUSTAND.AUSFALL;
}

/** Wann der aktuelle Zustand begann (ms seit Epoche) oder `null` im Gruenen. */
export function seitWann() {
  return seit;
}

/** Abonniert Zustandswechsel. Gibt die Abmeldefunktion zurueck. */
export function abonniere(fn) {
  abonnenten.add(fn);
  return () => abonnenten.delete(fn);
}

// ── Die Fenster-Ereignisse: der Weg aus "offline" ───────────────────────────
if (typeof window !== 'undefined') {
  window.addEventListener('offline', () => {
    // 🔴 R15-W1: den AUSSTEHENDEN Sonden-Timer MITNEHMEN. Ohne diese zwei Zeilen
    // feuert er nach dem Rueckzug, scheitert am Netz und setzt `ausfall` — das
    // Banner stuende ohne Netz ueber einer Aussage, die nichts belegt.
    if (sondeTimer) { clearTimeout(sondeTimer); sondeTimer = null; }
    setzeZustand(ZUSTAND.OFFLINE);
  });
  window.addEventListener('online', () => {
    if (zustand === ZUSTAND.OFFLINE) setzeZustand(ZUSTAND.ONLINE);
  });
}

/**
 * 🔴 Nur fuer Proben: setzt den Modulzustand zurueck (Timer, Zustand, Abos,
 * Klasse). Die Fenster-Listener bleiben — sie haengen an echten Ereignissen.
 */
export function _zuruecksetzen() {
  if (sondeTimer) { clearTimeout(sondeTimer); sondeTimer = null; }
  sondeLaeuft = false;
  rueckzugStufe = 0;
  seit = null;
  zustand = ZUSTAND.ONLINE;
  abonnenten.clear();
  setzeKlasse(false);
}
